<template>
  <div class="diagnostics-overlay">
    <button class="diagnostics-close" @click="$emit('close')">Close</button>

    <h2>Customer Display Diagnostics</h2>

    <table class="diagnostics-table">
      <tbody>
        <tr>
          <td>Fullscreen</td>
          <td>{{ kioskStatus.fullscreen ? "Active" : "Inactive" }}</td>
        </tr>
        <tr>
          <td>Wake Lock</td>
          <td>{{ kioskStatus.wakeLock ? "Held" : "Not held" }}</td>
        </tr>
        <tr>
          <td>Orientation Lock</td>
          <td>
            {{
              kioskStatus.orientationLocked
                ? "Locked (landscape)"
                : "Not locked"
            }}
          </td>
        </tr>
        <tr>
          <td>Websocket</td>
          <td>{{ connectionState.current }}</td>
        </tr>
        <tr>
          <td>Screen Resolution</td>
          <td>{{ screenWidth }} &times; {{ screenHeight }}</td>
        </tr>
      </tbody>
    </table>

    <div class="fullscreen-section">
      <button class="fullscreen-button" @click="$emit('request-fullscreen')">
        Request Fullscreen
      </button>
    </div>

    <div class="move-display-section">
      <button class="move-display-button" @click="moveToSecondaryDisplay">
        Move to Secondary Display
      </button>
      <p v-if="moveStatusMessage" class="move-status-message">
        {{ moveStatusMessage }}
      </p>
    </div>

    <div class="test-pattern">
      <div class="test-pattern-corner top-left"></div>
      <div class="test-pattern-corner top-right"></div>
      <div class="test-pattern-corner bottom-left"></div>
      <div class="test-pattern-corner bottom-right"></div>
      <div class="test-pattern-center">
        {{ screenWidth }} &times; {{ screenHeight }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { connectionState } from "@/kiosk/connectionStatus";

defineProps({
  kioskStatus: {
    type: Object,
    required: true,
  },
});

defineEmits(["close", "request-fullscreen"]);

const screenWidth = ref(window.screen.width);
const screenHeight = ref(window.screen.height);
const moveStatusMessage = ref("");

onMounted(() => {
  window.addEventListener("resize", () => {
    screenWidth.value = window.screen.width;
    screenHeight.value = window.screen.height;
  });
});

async function moveToSecondaryDisplay() {
  if (!("getScreenDetails" in window)) {
    moveStatusMessage.value =
      "Window Management API not supported in this browser.";
    return;
  }

  try {
    const screenDetails = await window.getScreenDetails();
    const secondary = screenDetails.screens.find(
      (candidate) => candidate !== screenDetails.currentScreen
    );

    if (!secondary) {
      moveStatusMessage.value = "No secondary screen detected.";
      return;
    }

    window.moveTo(secondary.availLeft, secondary.availTop);
    window.resizeTo(secondary.availWidth, secondary.availHeight);

    if (document.documentElement.requestFullscreen) {
      await document.documentElement.requestFullscreen({ screen: secondary });
    }

    moveStatusMessage.value = "Moved to secondary display.";
  } catch (error) {
    moveStatusMessage.value = `Failed to move: ${error.message}`;
  }
}
</script>

<style scoped>
.diagnostics-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  color: #fff;
  z-index: 9999;
  padding: 24px;
  font-family: monospace;
  overflow-y: auto;
}

.diagnostics-close {
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 8px 16px;
  cursor: pointer;
}

.diagnostics-table {
  border-collapse: collapse;
  margin-top: 24px;
}

.diagnostics-table td {
  border: 1px solid #555;
  padding: 8px 16px;
}

.fullscreen-section {
  margin-top: 24px;
}

.fullscreen-button {
  padding: 10px 20px;
  cursor: pointer;
}

.test-pattern {
  position: relative;
  margin-top: 32px;
  width: 100%;
  max-width: 640px;
  aspect-ratio: 16 / 9;
  border: 2px solid #0f0;
}

.test-pattern-corner {
  position: absolute;
  width: 24px;
  height: 24px;
  border: 2px solid #0f0;
}

.top-left {
  top: -2px;
  left: -2px;
  border-right: none;
  border-bottom: none;
}

.top-right {
  top: -2px;
  right: -2px;
  border-left: none;
  border-bottom: none;
}

.bottom-left {
  bottom: -2px;
  left: -2px;
  border-right: none;
  border-top: none;
}

.bottom-right {
  bottom: -2px;
  right: -2px;
  border-left: none;
  border-top: none;
}

.test-pattern-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 24px;
  color: #0f0;
}

.move-display-section {
  margin-top: 24px;
}

.move-display-button {
  padding: 10px 20px;
  cursor: pointer;
}

.move-status-message {
  margin-top: 8px;
  color: #0f0;
}
</style>
